/**
 * Safe localStorage utilities with quota management
 * Prevents QuotaExceededError crashes on mobile devices
 */

// Cache keys in priority order (least important first for eviction)
const CACHE_PRIORITY = [
  'profile_cache',
  'dashboard_cache',
  'challenges_cache', 
  'feed_cache_challenges',
  'feed_cache_all'
];

/**
 * Safely set item in localStorage with automatic quota management
 * @param {string} key - Storage key
 * @param {any} value - Value to store (will be JSON stringified)
 * @returns {boolean} - Whether the operation succeeded
 */
export const safeSetItem = (key, value) => {
  const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
  
  try {
    localStorage.setItem(key, stringValue);
    return true;
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
      console.warn(`[Storage] Quota exceeded when saving ${key}, attempting cleanup...`);
      
      // Try clearing caches in priority order
      for (const cacheKey of CACHE_PRIORITY) {
        if (cacheKey === key) continue; // Don't clear what we're trying to save
        
        try {
          const existed = localStorage.getItem(cacheKey) !== null;
          if (existed) {
            localStorage.removeItem(cacheKey);
            console.log(`[Storage] Cleared ${cacheKey} to free space`);
            
            // Try saving again
            localStorage.setItem(key, stringValue);
            console.log(`[Storage] Successfully saved ${key} after cleanup`);
            return true;
          }
        } catch (retryErr) {
          console.error('[Storage] Retry failed:', retryErr.message);
        }
      }
      
      // If still failing, try to reduce the data size
      if (typeof value === 'object' && value !== null) {
        // If it has a posts/items array, try saving fewer items
        const reducibleKeys = ['posts', 'items', 'data'];
        for (const arrayKey of reducibleKeys) {
          if (Array.isArray(value[arrayKey]) && value[arrayKey].length > 5) {
            try {
              const reducedValue = {
                ...value,
                [arrayKey]: value[arrayKey].slice(0, 5)
              };
              localStorage.setItem(key, JSON.stringify(reducedValue));
              console.log(`[Storage] Saved ${key} with reduced data (5 items)`);
              return true;
            } catch (finalErr) {
              console.error('[Storage] Reduce failed:', finalErr.message);
            }
          }
        }
      }
      
      console.warn(`[Storage] Could not save ${key} - storage full`);
      return false;
    }
    
    console.error(`[Storage] Error saving ${key}:`, e);
    return false;
  }
};

/**
 * Safely get item from localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist or parse fails
 * @returns {any} - Parsed value or default
 */
export const safeGetItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch (e) {
    console.warn(`[Storage] Error reading ${key}:`, e);
    return defaultValue;
  }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - Storage key
 */
export const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`[Storage] Error removing ${key}:`, e);
  }
};

/**
 * Clear all app caches (useful for logout or manual reset)
 */
export const clearAllCaches = () => {
  for (const key of CACHE_PRIORITY) {
    safeRemoveItem(key);
  }
  console.log('[Storage] All caches cleared');
};

/**
 * Get estimated storage usage
 * @returns {Object} - { used, total, percentage }
 */
export const getStorageInfo = () => {
  try {
    let used = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      used += key.length + (value?.length || 0);
    }
    
    // Most browsers have 5MB limit
    const total = 5 * 1024 * 1024;
    const percentage = Math.round((used / total) * 100);
    
    return { used, total, percentage };
  } catch (e) {
    return { used: 0, total: 5 * 1024 * 1024, percentage: 0 };
  }
};
